import React, { useEffect, useMemo, useState } from "react";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { IconButton, Stack } from "@mui/material";
import { useOpportunitiesSelect } from "store";

import { Opportunity } from "./Opportunity";

interface Props {
  isTableLoading: boolean;
}

export const OpportunityBlock: React.FC<Props> = ({ isTableLoading }) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [slideDirection, setSlideDirection] = useState<"left" | "right" | null>(null);
  const [prevOpportunity, setPrevOpportunity] = useState<typeof currentOpportunity>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const opportunities = useOpportunitiesSelect();
  // const navigate = useNavigate();
  const currentOpportunity = useMemo(() => {
    if (!opportunities?.length) return null;
    return opportunities[currentIndex];
  }, [opportunities, currentIndex]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setIsExpanded(false);
      setPrevOpportunity(currentOpportunity);
      setSlideDirection("right");
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (opportunities && currentIndex < opportunities.length - 1) {
      setIsExpanded(false);
      setPrevOpportunity(currentOpportunity);
      setSlideDirection("left");
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleAnimationEnd = () => {
    setSlideDirection(null);
    setPrevOpportunity(null);
  };

  useEffect(() => {
    if (opportunities?.length && !currentOpportunity) {
      setCurrentIndex(0);
      setPrevOpportunity(null);
      setIsExpanded(false);
    }
  }, [opportunities, currentOpportunity]);

  if (!currentOpportunity || isTableLoading) return null;

  return (
    <Stack sx={{ position: "relative" }}>
      {currentIndex > 0 && (
        <IconButton
          onClick={handlePrevious}
          sx={{
            position: "absolute",
            backgroundColor: "#DBE8F5",
            height: "24px",
            width: "24px",
            left: "-10px",
            top: "30px",
            zIndex: 10
          }}
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
      )}

      <Stack sx={{ overflow: "hidden", borderRadius: "8px" }}>
        {prevOpportunity && (
          <Stack
            sx={{
              position: "absolute",
              width: "100%",
              animation: `slide-${slideDirection}-exit 0.5s ease-in-out forwards`,
              "@keyframes slide-left-exit": {
                "0%": { transform: "translateX(0)" },
                "100%": { transform: "translateX(-100%)" }
              },
              "@keyframes slide-right-exit": {
                "0%": { transform: "translateX(0)" },
                "100%": { transform: "translateX(100%)" }
              }
            }}
          >
            <Opportunity opportunity={prevOpportunity} isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
          </Stack>
        )}

        <Stack
          onAnimationEnd={handleAnimationEnd}
          sx={{
            animation: slideDirection ? `slide-${slideDirection}-enter 0.5s ease-in-out forwards` : "none",
            "@keyframes slide-left-enter": {
              "0%": { transform: "translateX(100%)" },
              "100%": { transform: "translateX(0)" }
            },
            "@keyframes slide-right-enter": {
              "0%": { transform: "translateX(-100%)" },
              "100%": { transform: "translateX(0)" }
            }
          }}
        >
          <Opportunity opportunity={currentOpportunity} isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
        </Stack>
      </Stack>
      {/* <Stack alignItems="flex-end" sx={{ paddingTop: "10px" }}>
        <Typography
          onClick={() => navigate({ route: "opportunitiesAnalysis" })}
          sx={{ color: "#26A6AE", fontSize: "16px", fontWeight: "400", cursor: "pointer" }}
        >
          See all opportunities
        </Typography>
      </Stack> */}
      {opportunities && currentIndex < opportunities.length - 1 && (
        <IconButton
          onClick={handleNext}
          sx={{
            position: "absolute",
            backgroundColor: "#DBE8F5",
            height: "24px",
            width: "24px",
            right: "-10px",
            top: "30px",
            zIndex: 10
          }}
        >
          <KeyboardArrowRightIcon />
        </IconButton>
      )}
    </Stack>
  );
};
